import { create } from 'zustand';
import type { Routine } from '@/hooks/useRoutines';
import {
  normalizeDeliveryRows,
  type RoutineDeliveryRow,
} from '@/components/routines/routineChannelDisplay';
import { routineSourceApp } from '@/components/routines/routineSourceDisplay';
import { describeRoutineCadence } from '@/constants/routines';

/**
 * Live state of the routine preview panel, driven by the agent's
 * `ui_routine_draft` tool calls.
 *
 * That tool renders nothing in the transcript — chat.tsx intercepts its
 * streamed arguments and writes them here instead (the same shape the panel
 * consumes, so this type IS the tool's client-side contract). Every call
 * carries a FULL snapshot, so writes are whole-state replaces: a field the
 * agent omits is genuinely unknown, not unchanged.
 *
 * Keyed by chat session (`<workflowInstanceId>-<executionId>`) so switching
 * threads can never show another routine's draft.
 */
export interface RoutineDraftSnapshot {
  status: 'draft' | 'creating' | 'created' | 'updated';
  title: string | null;
  description: string | null;
  alert_logic: string | null;
  /**
   * `app_short_name` is null when the source's provider can't be identified
   * (a warehouse-backed dataset or prism query) — those rows get a neutral
   * glyph rather than a connector logo.
   */
  sources: { app_short_name: string | null; label: string }[] | null;
  schedule_label: string | null;
  /**
   * Already normalized by {@link toRoutineDraftSnapshot} — the panel renders
   * these rows straight through rather than re-validating them.
   */
  delivers_to: RoutineDeliveryRow[] | null;
  routine_id: string | null;
}

interface RoutineDraftState {
  drafts: Record<string, RoutineDraftSnapshot>;
  /**
   * True between `TOOL_STREAM_START` and the first complete snapshot for a
   * session — the panel shows a skeleton while the agent's first draft is
   * still arriving.
   */
  streaming: Record<string, boolean>;
  setDraft: (sessionKey: string, snapshot: RoutineDraftSnapshot) => void;
  setStreaming: (sessionKey: string, isStreaming: boolean) => void;
  clearDraft: (sessionKey: string) => void;
}

export const useRoutineDraftStore = create<RoutineDraftState>((set) => ({
  drafts: {},
  streaming: {},
  setDraft: (sessionKey, snapshot) =>
    set((state) => ({
      drafts: { ...state.drafts, [sessionKey]: snapshot },
      streaming: { ...state.streaming, [sessionKey]: false },
    })),
  // Every chat stream releases the flag on done/error, routine thread or not,
  // so a no-op write must stay a no-op: it neither copies the map nor inserts a
  // key for a session that never had a draft.
  setStreaming: (sessionKey, isStreaming) =>
    set((state) =>
      Boolean(state.streaming[sessionKey]) === isStreaming
        ? state
        : { streaming: { ...state.streaming, [sessionKey]: isStreaming } },
    ),
  clearDraft: (sessionKey) =>
    set((state) => {
      const drafts = { ...state.drafts };
      const streaming = { ...state.streaming };
      delete drafts[sessionKey];
      delete streaming[sessionKey];
      return { drafts, streaming };
    }),
}));

const DRAFT_STATUSES = new Set<RoutineDraftSnapshot['status']>([
  'draft',
  'creating',
  'created',
  'updated',
]);

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Coerce raw `ui_routine_draft` arguments into a snapshot.
 *
 * The panel renders straight from LLM-streamed tool args, so this picks exactly
 * the fields it knows and drops everything else — the tool's grouping fields,
 * and any key the model invents. Anything of the wrong type reads as unknown
 * rather than reaching the panel and breaking a row.
 *
 * @param args - Parsed tool-call arguments.
 *
 * @returns A snapshot safe to render.
 */
export function toRoutineDraftSnapshot(args: Record<string, unknown>): RoutineDraftSnapshot {
  const status = args.status as RoutineDraftSnapshot['status'];
  const sources = Array.isArray(args.sources)
    ? args.sources.flatMap((source): RoutineDraftSnapshot['sources'] & object => {
      if (typeof source !== 'object' || source === null) return [];
      const { app_short_name, label } = source as Record<string, unknown>;
      const resolvedLabel = asString(label);
      return resolvedLabel ? [{ app_short_name: asString(app_short_name), label: resolvedLabel }] : [];
    })
    : null;
  // The one place delivery rows are validated — the shared normalizer, so the
  // panel and the in-chat card accept exactly the same rows.
  const deliversTo = normalizeDeliveryRows(args.delivers_to);

  return {
    status: DRAFT_STATUSES.has(status) ? status : 'draft',
    title: asString(args.title),
    description: asString(args.description),
    alert_logic: asString(args.alert_logic),
    sources: sources?.length ? sources : null,
    schedule_label: asString(args.schedule_label),
    delivers_to: deliversTo.length ? deliversTo : null,
    routine_id: asString(args.routine_id),
  };
}

/**
 * Which of the panel's details are filled in. Title and description are one
 * detail (they're generated together from the same ask), so the list matches
 * the flow's questions: what to name it, what to alert on, which sources, when
 * to run, where to deliver.
 */
function detailsFilled(snapshot: RoutineDraftSnapshot): boolean[] {
  return [
    Boolean(snapshot.title ?? snapshot.description),
    Boolean(snapshot.alert_logic),
    Boolean(snapshot.sources?.length),
    Boolean(snapshot.schedule_label),
    Boolean(snapshot.delivers_to?.length),
  ];
}

/** A draft with nothing filled in — the create flow's starting state. */
export function emptyRoutineDraft(): RoutineDraftSnapshot {
  return toRoutineDraftSnapshot({});
}

/** How many of the {@link ROUTINE_DRAFT_DETAIL_TOTAL} panel details are set. */
export function countDetailsSet(snapshot: RoutineDraftSnapshot): number {
  return detailsFilled(snapshot).filter(Boolean).length;
}

/** Denominator of the panel's progress footer — derived so it can't disagree. */
export const ROUTINE_DRAFT_DETAIL_TOTAL = detailsFilled(emptyRoutineDraft()).length;

/**
 * Seed the panel from an existing routine so the edit flow shows the real
 * routine the instant the chat opens, before the agent has said anything.
 *
 * Source attribution is the shared `routineSourceApp` rule — dataset and prism
 * sources stay unresolved and get a neutral glyph rather than being badged as
 * Strive's own.
 *
 * External channel detail is still lossy: configs store Slack channel ids and
 * Google Chat space resource names, not the display names the panel wants, so
 * those rows show the provider alone until the agent's first snapshot fills in
 * the names.
 */
export function buildSnapshotFromRoutine(routine: Routine): RoutineDraftSnapshot {
  const dataSources = routine.definition?.data_sources ?? [];
  const externalChannels = routine.output_channels?.channels ?? [];

  return {
    status: 'created',
    title: routine.title ?? null,
    description: routine.description ?? null,
    alert_logic: routine.definition?.evaluation?.condition ?? null,
    sources: dataSources.length
      ? dataSources.map((source) => ({
        app_short_name: routineSourceApp(source),
        label: source.display_name,
      }))
      : null,
    schedule_label: describeRoutineCadence(routine.schedule.cron),
    // The platform always posts the in-app card, so Strive Inbox is always a
    // destination — it is not stored in `output_channels`.
    delivers_to: [
      { channel: 'strive_inbox' as const, detail: 'Inbox' },
      ...externalChannels.map((entry) => ({ channel: entry.channel, detail: null })),
    ],
    routine_id: routine.id,
  };
}
